// src/components/NotificationBell.js
import React, { useState, useEffect, useRef } from 'react';

// Notification type icons
const notificationIcons = {
  welcome: '🌱',
  sample_verified: '✅',
  sample_rejected: '❌',
  sample_submitted: '🧪',
  plot_created: '📍',
  carbon_update: '💰',
  payment: '💵',
  microgrant: '🎁',
  visit_scheduled: '📅',
  weather_alert: '🌤️',
  new_farmer: '👤',
  task_reminder: '⏰',
  system: '🔔',
  default: '📬'
};

export default function NotificationBell() {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);

  // Fetch notifications
  const fetchNotifications = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('https://agroconnect.io/api/notifications?limit=20', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      if (data.ok) {
        setNotifications(data.notifications || []);
        setUnreadCount(data.unreadCount || 0);
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  };

  // Mark single notification as read
  const markAsRead = async (id) => {
    try {
      const token = localStorage.getItem('token');
      await fetch(`https://agroconnect.io/api/notifications/${id}/read`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      setNotifications(prev => 
        prev.map(n => n.id === id ? { ...n, is_read: 1 } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking as read:', error);
    }
  };

  // Mark all as read
  const markAllAsRead = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      await fetch('https://agroconnect.io/api/notifications/read-all', {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      setNotifications(prev => prev.map(n => ({ ...n, is_read: 1 })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Error marking all as read:', error);
    } finally {
      setLoading(false);
    }
  };

  // Delete notification
  const deleteNotification = async (id, e) => {
    e.stopPropagation();
    try {
      const token = localStorage.getItem('token');
      await fetch(`https://agroconnect.io/api/notifications/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      const notification = notifications.find(n => n.id === id);
      if (notification && !notification.is_read) {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
      setNotifications(prev => prev.filter(n => n.id !== id));
    } catch (error) {
      console.error('Error deleting notification:', error);
    }
  };

  // Handle notification click
  const handleNotificationClick = (notification) => {
    if (!notification.is_read) {
      markAsRead(notification.id);
    }
    if (notification.link) {
      window.location.href = notification.link;
    }
    setIsOpen(false);
  };

  // Format time ago
  const timeAgo = (dateString) => {
    const now = new Date();
    const date = new Date(dateString);
    const seconds = Math.floor((now - date) / 1000);
    
    if (seconds < 60) return 'Just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
    return date.toLocaleDateString();
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fetch on mount and every 30 seconds
  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div ref={dropdownRef} style={{ position: 'relative' }}>
      {/* Bell Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        style={{
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          padding: '8px 12px',
          borderRadius: '8px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'background 0.2s',
          position: 'relative'
        }}
        onMouseEnter={(e) => e.target.style.background = 'rgba(74, 222, 128, 0.1)'}
        onMouseLeave={(e) => e.target.style.background = 'transparent'}
        aria-label="Notifications"
      >
        <span style={{ fontSize: '24px' }}>🔔</span>
        
        {/* Unread Badge */}
        {unreadCount > 0 && (
          <span style={{
            position: 'absolute',
            top: '2px',
            right: '6px',
            background: '#ef4444',
            color: 'white',
            fontSize: '11px',
            fontWeight: 'bold',
            padding: '2px 6px',
            borderRadius: '10px',
            minWidth: '18px',
            textAlign: 'center'
          }}>
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div style={{
          position: 'absolute',
          top: '100%',
          right: '0',
          marginTop: '8px',
          width: '360px',
          maxHeight: '480px',
          background: '#1a3327',
          border: '1px solid #2d5a45',
          borderRadius: '12px',
          boxShadow: '0 10px 40px rgba(0,0,0,0.3)',
          overflow: 'hidden',
          zIndex: 1000
        }}>
          {/* Header */}
          <div style={{
            padding: '16px',
            borderBottom: '1px solid #2d5a45',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <h3 style={{ margin: 0, color: '#4ade80', fontSize: '16px' }}>
              Notifications
              {unreadCount > 0 && (
                <span style={{ 
                  marginLeft: '8px', 
                  fontSize: '12px', 
                  color: '#888' 
                }}>
                  ({unreadCount} new)
                </span>
              )}
            </h3>
            {unreadCount > 0 && (
              <button
                onClick={markAllAsRead}
                disabled={loading}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: '#4ade80',
                  fontSize: '12px',
                  cursor: 'pointer',
                  padding: '4px 8px',
                  borderRadius: '4px'
                }}
              >
                {loading ? 'Marking...' : 'Mark all read'}
              </button>
            )}
          </div>

          {/* Notifications List */}
          <div style={{
            maxHeight: '400px',
            overflowY: 'auto'
          }}>
            {notifications.length === 0 ? (
              <div style={{
                padding: '40px 20px',
                textAlign: 'center',
                color: '#888'
              }}>
                <span style={{ fontSize: '48px', display: 'block', marginBottom: '12px' }}>📭</span>
                No notifications yet
              </div>
            ) : (
              notifications.map((notification) => (
                <div
                  key={notification.id}
                  onClick={() => handleNotificationClick(notification)}
                  style={{
                    padding: '12px 16px',
                    borderBottom: '1px solid #2d5a45',
                    cursor: notification.link ? 'pointer' : 'default',
                    background: notification.is_read ? 'transparent' : 'rgba(74, 222, 128, 0.05)',
                    transition: 'background 0.2s',
                    display: 'flex',
                    gap: '12px',
                    alignItems: 'flex-start'
                  }}
                  onMouseEnter={(e) => {
                    if (notification.link) {
                      e.currentTarget.style.background = 'rgba(74, 222, 128, 0.1)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = notification.is_read 
                      ? 'transparent' 
                      : 'rgba(74, 222, 128, 0.05)';
                  }}
                >
                  {/* Icon */}
                  <span style={{ fontSize: '24px', flexShrink: 0 }}>
                    {notificationIcons[notification.type] || notificationIcons.default}
                  </span>
                  
                  {/* Content */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      gap: '8px'
                    }}>
                      <span style={{
                        fontWeight: notification.is_read ? 'normal' : 'bold',
                        color: notification.is_read ? '#888' : '#fff',
                        fontSize: '14px'
                      }}>
                        {notification.title}
                      </span>
                      
                      {/* Delete button */}
                      <button
                        onClick={(e) => deleteNotification(notification.id, e)}
                        style={{
                          background: 'transparent',
                          border: 'none',
                          color: '#666',
                          cursor: 'pointer',
                          padding: '2px 6px',
                          fontSize: '12px',
                          borderRadius: '4px',
                          flexShrink: 0
                        }}
                        onMouseEnter={(e) => e.target.style.color = '#ef4444'}
                        onMouseLeave={(e) => e.target.style.color = '#666'}
                      >
                        ✕
                      </button>
                    </div>
                    
                    <p style={{
                      margin: '4px 0 0 0',
                      color: '#888',
                      fontSize: '13px',
                      lineHeight: '1.4'
                    }}>
                      {notification.message}
                    </p>
                    
                    <span style={{
                      fontSize: '11px',
                      color: '#666',
                      marginTop: '6px',
                      display: 'block'
                    }}>
                      {timeAgo(notification.created_at)}
                    </span>
                  </div>
                  
                  {/* Unread indicator */}
                  {!notification.is_read && (
                    <span style={{
                      width: '8px',
                      height: '8px',
                      background: '#4ade80',
                      borderRadius: '50%',
                      flexShrink: 0,
                      marginTop: '6px'
                    }} />
                  )}
                </div>
              ))
            )}
          </div>

          {/* Footer */}
          {notifications.length > 0 && (
            <div style={{
              padding: '12px 16px',
              borderTop: '1px solid #2d5a45',
              textAlign: 'center'
            }}>
              <button
                onClick={() => {
                  setIsOpen(false);
                  window.location.href = '/notifications';
                }}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: '#4ade80',
                  fontSize: '13px',
                  cursor: 'pointer'
                }}
              >
                View all notifications →
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}